import { cva } from "class-variance-authority";
import {
  ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useClickAway } from "react-use";
import { DrawerSelectButtonTrigger } from "./drawer-select-button-trigger";
import { DrawerSelectContent } from "./drawer-select-content";
import { DrawerSelectGroup } from "./drawer-select-group";
import { DrawerSelectOption } from "./drawer-select-option";
import { DrawerSelectSearchInput } from "./drawer-select-search-input";
import { DrawerSelectTrigger } from "./drawer-select-trigger";
import {
  DrawerSelectContext,
  DrawerSelectVariant,
} from "./drawer-select.context";
import styles from "./drawer-select.module.css";
import { DrawerSelectOptionType } from "./drawer-select.types";

const drawerSelectCva = cva([styles.DrawerSelect], {
  variants: {
    variant: {
      modal: styles.Modal,
      inline: styles.Inline,
    },
  },
});

export type DrawerSelectProps = Omit<
  ComponentProps<"div">,
  "value" | "onChange" | "onSelect"
> & {
  variant?: DrawerSelectVariant;
  /** Quando true, permite selecionar múltiplas opções (exibe botões Confirmar/Limpar). Quando false, seleção única ao clicar (sem botões, exceto se variant="modal"). */
  multiple?: boolean;
  /** Número máximo de opções que podem ser selecionadas quando multiple=true. Se undefined, não há limite. */
  maxOptions?: number;
  label?: string;
  /** Quando definido, este valor é usado para filtrar as opções no lugar do texto do DrawerSelect.SearchInput. */
  query?: string;
  onQueryChange?: (query: string) => void;
  value?: DrawerSelectOptionType[];
  onChange?: (value: DrawerSelectOptionType[]) => void;
  onSelect?: (value: DrawerSelectOptionType[]) => void;
  applyButtonText?: string;
  clearButtonText?: string;
  modalTitle?: string;
  /** Altura máxima do corpo do modal (ex: "70vh", "400px"). Quando definido, o conteúdo do modal rola ao exceder. */
  modalMaxHeight?: string;
  /** Quando há busca ativa, número máximo de opções exibidas. Se não definido, todas as opções que passam no filtro são exibidas. */
  searchLimit?: number;
  isLoading?: boolean;
};

export function DrawerSelect({
  variant = "inline",
  multiple = false,
  maxOptions,
  label,
  query,
  onQueryChange,
  value,
  onChange,
  className,
  children,
  applyButtonText,
  clearButtonText,
  modalTitle,
  modalMaxHeight,
  searchLimit,
  onSelect,
  isLoading,
  ...props
}: DrawerSelectProps) {
  const [selectedOptions, setSelectedOptions] = useState<
    DrawerSelectOptionType[]
  >(value ?? []);
  const [options, setOptions] = useState<DrawerSelectOptionType[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const domRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    onSelect?.(selectedOptions);
  }, [JSON.stringify(selectedOptions), isOpen]);

  const closeDrawer = useCallback(() => {
    setSelectedOptions([]);
    onQueryChange?.("");
    setIsOpen(false);
  }, [value, onQueryChange]);

  useClickAway(domRef, () => {
    if (isOpen) {
      closeDrawer();
    }
  });

  const handleQueryValueChange = useCallback(
    (query: string) => {
      onQueryChange?.(query);
    },
    [onQueryChange],
  );

  const handleApply = useCallback(() => {
    onChange?.(selectedOptions);
    closeDrawer();
  }, [selectedOptions, onChange, closeDrawer]);

  const handleClear = useCallback(() => {
    setSelectedOptions([]);
    handleQueryValueChange("");
  }, [handleQueryValueChange]);

  const handleClearValue = useCallback(() => {
    setSelectedOptions([]);
    handleQueryValueChange("");
    closeDrawer();
    onChange?.([]);
  }, [handleQueryValueChange, onChange, closeDrawer]);

  const pushOption = useCallback((option: DrawerSelectOptionType) => {
    setOptions((prev) => [...prev, option]);
  }, []);

  const removeOption = useCallback((option: DrawerSelectOptionType) => {
    setOptions((prev) => prev.filter((o) => o.value !== option.value));
  }, []);

  const selectSingleOption = useCallback(
    (option: DrawerSelectOptionType) => {
      const next = [option];
      setSelectedOptions(next);
      onChange?.(next);
      closeDrawer();
    },
    [onChange, closeDrawer],
  );

  const selectOnlyOption = useCallback((option: DrawerSelectOptionType) => {
    setSelectedOptions([option]);
  }, []);

  const toggleOption = useCallback(
    (option: DrawerSelectOptionType) => {
      setSelectedOptions((prev) => {
        if (prev.some((o) => o.value === option.value)) {
          return prev.filter((o) => o.value !== option.value);
        }
        if (maxOptions !== undefined && prev.length >= maxOptions) {
          return prev;
        }
        return [...prev, option];
      });
    },
    [maxOptions],
  );

  const isOptionSelected = useCallback(
    (option: DrawerSelectOptionType) =>
      selectedOptions.some((o) => {
        const cOptionValue = String(option.value);
        const cValue = String(o.value);

        return cValue.toLowerCase() === cOptionValue.toLowerCase();
      }),
    [selectedOptions],
  );

  const maxOptionsReached = useMemo(() => {
    return (
      multiple &&
      maxOptions !== undefined &&
      selectedOptions.length >= maxOptions
    );
  }, [multiple, maxOptions, selectedOptions]);

  const contextValue = useMemo(
    () => ({
      isOpen,
      setIsOpen,
      query,
      handleQueryValueChange,
      pushOption,
      removeOption,
      toggleOption,
      selectSingleOption,
      selectOnlyOption,
      handleApply,
      handleClear,
      variant,
      multiple,
      maxOptions,
      options,
      label,
      isOptionSelected,
      value: value ?? selectedOptions,
      applyButtonText,
      clearButtonText,
      modalTitle,
      modalMaxHeight,
      searchLimit,
      maxOptionsReached,
      handleClearValue,
      isLoading,
    }),
    [
      isOpen,
      query,
      handleQueryValueChange,
      pushOption,
      removeOption,
      toggleOption,
      selectSingleOption,
      selectOnlyOption,
      handleApply,
      handleClear,
      variant,
      multiple,
      maxOptions,
      options,
      label,
      isOptionSelected,
      value,
      selectedOptions,
      applyButtonText,
      clearButtonText,
      modalTitle,
      modalMaxHeight,
      searchLimit,
      maxOptionsReached,
      handleClearValue,
      isLoading,
    ],
  );

  useEffect(() => {
    if (isOpen) {
      setSelectedOptions(value ?? []);

      if (!multiple) {
        const valueLabel = value?.map((v) => v.text).join(", ") ?? "";
        onQueryChange?.(valueLabel);
      }
    }
  }, [isOpen, JSON.stringify(value), multiple]);

  return (
    <DrawerSelectContext.Provider value={contextValue}>
      <div
        ref={domRef}
        className={drawerSelectCva({ className, variant })}
        {...props}
      >
        {children}
      </div>
    </DrawerSelectContext.Provider>
  );
}

export namespace DrawerSelect {
  export const ButtonTrigger = DrawerSelectButtonTrigger;
  export const SearchInput = DrawerSelectSearchInput;
  export const Content = DrawerSelectContent;
  export const Trigger = DrawerSelectTrigger;
  export const Option = DrawerSelectOption;
  export const Group = DrawerSelectGroup;
}
